import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Chip } from '@elementary/common-ui/components';
import SelectDatasetPage from '@elementary/common-ui/components/SelectDatasetPage';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useUrlFilters, {
  UrlFilters,
} from '@elementary/common-ui/hooks/useUrlFilters';
import { Box, Stack, CircularProgress } from '@mui/material';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  LINEAGE_STREAM_DIRECTION,
  HOP_LIMIT,
  LINEAGE_FILTERS,
} from '@elementary/common-ui/constants/urlParams';
import ReactFlow, {
  Background,
  BackgroundVariant,
  useNodesState,
  ReactFlowInstance,
  Node as ReactFlowNode,
  ReactFlowProvider,
  useStore,
} from 'reactflow';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import type { Report } from '../../types/types';
import { radius, space, componentToken } from '../../style/utils';
import useLineageData from './hooks/useLineageData';
import useSyncCompactNodeInternals from './hooks/useSyncCompactNodeInternals';
import {
  ApiLineage,
  DagDepthBoundary,
  HopLimit,
  LineageColorBy,
  ReactFlowNodeData,
  StreamDirection,
} from './types';
import {
  areLineageNodeDegreesEqual,
  filterReactFlowChildNodes,
  getLineageNodeDegrees,
  getLineageZoomTierGraph,
  selectLineageZoomTier,
} from './utils/lineageGraphUtils';
import { LineageExporter } from './export';
import GraphChildNode from './GraphChildNode';
import GraphNode from './GraphNode';
import LegendBase from './Legend';
import LineageGraphControls from './LineageGraphControls';
import 'reactflow/dist/base.css';
import {
  LineageGraphProvider,
  useLineageGraphContext,
} from './LineageGraphContext';
import LineageInfoPanel from './LineageInfoPanel';
import StreamSelector from './HopsControl/StreamSelector';

const zoomLimitThreshold = 200;
const zoomLimit = 0.7;

const Wrapper = styled(Box)<{ $isCompactTier: boolean }>`
  width: 100%;
  height: 100%;

  ${({ $isCompactTier }) =>
    $isCompactTier &&
    `
    .react-flow__node-graphNode {
      pointer-events: none !important;
    }
  `}

  & .react-flow__attribution {
    display: none;
  }

  .react-flow__minimap-mask {
    stroke: ${({ theme }) => theme.palette.status.error.bright};
    stroke-width: 8px;
  }

  .react-flow__minimap {
    border: 1.5px solid ${componentToken('lineage.minimapBorder')};
  }

  .react-flow__edge {
    pointer-events: none;
  }

  .MuiCircularProgress-circle {
    stroke: ${({ theme }) => theme.palette.accent.link};
  }
`;

const Legend = styled(LegendBase)`
  position: absolute;
  bottom: 24px;
  right: 24px;
  z-index: ${({ theme }) => theme.zIndex.drawer};
`;

const HopsControlOverlay = styled(Stack)`
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: ${({ theme }) => theme.zIndex.drawer};
  background: ${({ theme }) => theme.palette.surface.base};
  padding: ${space('lg')};
  border-radius: ${radius('lg')};
  border: 1px solid ${({ theme }) => theme.palette.border.light};
`;

const CenteringWrapper = styled(Stack)`
  position: absolute;
  align-items: center;
  justify-content: center;
  top: 30px;
  left: 0;
  width: 100%;
  height: 100%;
`;

const InteractiveCenteringWrapper = styled(CenteringWrapper)`
  z-index: 4; // Above the graph to enable clicking but below controls and loading
`;

type BaseLineageGraphProps = {
  reportData?: Report;
  onDagDepthBoundaryChange?: (val: DagDepthBoundary) => void;
};

export type UncontrolledLineageGraphProps = BaseLineageGraphProps & {
  isControlled?: false;
};

type ControlledLineageGraphBaseProps = BaseLineageGraphProps & {
  isControlled: true;
  overrideAssetIds?: string[];
  overrideColumnIds?: string[];
  overrideLineageData?: ApiLineage | null;
  overrideFilters?: UrlFilters;
  overrideStreamDirection?: StreamDirection;
  overrideHopLimit?: HopLimit;
  overrideColorBy?: LineageColorBy;
};

/**
 * When `showOverlayControls: true` we render BOTH the hops control and the legend
 * together.
 */
type ControlledLineageGraphPropsWithOverlayControls =
  ControlledLineageGraphBaseProps & {
    showOverlayControls: true;
    overrideDagDepthBoundary: DagDepthBoundary;
    onHopLimitChange: (val: HopLimit) => void;
    legendSelectedLabel?: string;
  };

type ControlledLineageGraphPropsWithoutOverlayControls =
  ControlledLineageGraphBaseProps & {
    showOverlayControls?: false;
  };

export type ControlledLineageGraphProps =
  | ControlledLineageGraphPropsWithOverlayControls
  | ControlledLineageGraphPropsWithoutOverlayControls;

export type LineageGraphProps =
  | UncontrolledLineageGraphProps
  | ControlledLineageGraphProps;

const LineageGraphContent = (props: LineageGraphProps) => {
  const { reportData, onDagDepthBoundaryChange } = props;
  const isControlled = props.isControlled === true;
  const controlled = isControlled ? props : null;

  const overrideAssetIds = controlled?.overrideAssetIds;
  const overrideColumnIds = controlled?.overrideColumnIds;
  const overrideLineageData = controlled?.overrideLineageData;
  const overrideFilters = controlled?.overrideFilters;
  const overrideStreamDirection = controlled?.overrideStreamDirection;
  const overrideHopLimit = controlled?.overrideHopLimit;
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance>();
  const [graphNodes, setGraphNodes, onNodesChange] =
    useNodesState<ReactFlowNodeData>([]);
  const {
    highlightedNode,
    setExceededColumnLimitAssets,
    setNodeDegrees,
    columnSearchByNodeId,
  } = useLineageGraphContext();

  const [searchParams] = useSearchParams();
  const { urlFilters: urlFiltersFromHook } = useUrlFilters({
    urlKey: LINEAGE_FILTERS,
  });

  const urlFilters = useMemo(
    () => (isControlled ? overrideFilters : urlFiltersFromHook),
    [isControlled, overrideFilters, urlFiltersFromHook],
  );

  const streamDirectionFromUrl = useMemo(
    () =>
      searchParams.get(LINEAGE_STREAM_DIRECTION) as StreamDirection | undefined,
    [searchParams],
  );

  const hopLimitFromUrl = useMemo(
    () =>
      (searchParams.get(HOP_LIMIT) &&
        JSON.parse(searchParams.get(HOP_LIMIT)!)) as HopLimit | undefined,
    [searchParams],
  );

  const streamDirection = isControlled
    ? overrideStreamDirection
    : streamDirectionFromUrl;

  const hopLimit = isControlled ? overrideHopLimit : hopLimitFromUrl;

  const {
    selectedModelColumn,
    selectedModelColumnType,
    selectedTreeNode,
    selectedTreeFolderNodes,
  } = useSelectedTreeItem();

  const { assetIds, columnIds } = useMemo(() => {
    if (isControlled) {
      return {
        assetIds: overrideAssetIds ?? [],
        columnIds: overrideColumnIds ?? [],
      };
    }

    if (selectedTreeNode?.resourceType !== 'folder') {
      if (selectedModelColumn) {
        const isColumn = selectedModelColumnType === 'column';
        return {
          assetIds: isColumn ? [] : [selectedModelColumn],
          columnIds: isColumn ? [selectedModelColumn] : [],
        };
      }
      if (selectedTreeNode) {
        return {
          assetIds: [selectedTreeNode.id],
          columnIds: [],
        };
      }
    }

    // A folder selection (or no selection at all, treated as all top-level
    // folders) resolves to every leaf asset under that scope.
    return {
      assetIds: selectedTreeFolderNodes?.map((node) => node.id) ?? [],
      columnIds: [],
    };
  }, [
    isControlled,
    overrideAssetIds,
    overrideColumnIds,
    selectedModelColumn,
    selectedModelColumnType,
    selectedTreeNode,
    selectedTreeFolderNodes,
  ]);

  const forceLoadDeps = useMemo(
    () =>
      JSON.stringify({
        assetIds,
        columnIds,
        urlFilters,
        streamDirection,
        hopLimit,
        isControlled,
        overrideLineageData,
      }),
    [
      assetIds,
      columnIds,
      urlFilters,
      streamDirection,
      hopLimit,
      isControlled,
      overrideLineageData,
    ],
  );

  const [forceLoadLargeGraph, setForceLoadLargeGraph] = useState(false);

  useEffect(() => {
    setForceLoadLargeGraph(false);
  }, [forceLoadDeps]);

  const {
    nodes,
    edges,
    onToggleExpandNode,
    onToggleHighlightNode,
    dagDepthBoundary,
    loading: lineageDataLoading,
    hitGraphLimits,
    exceededLimitParentAssets,
  } = useLineageData({
    reportData,
    graphNodes,
    assetIds,
    columnIds,
    filters: urlFilters,
    streamDirection,
    hopLimit,
    forceLoadLargeGraph,
    overrideLineageData,
  });

  const nodeTypes = useMemo(
    () => ({
      graphNode: GraphNode,
      graphChildNode: GraphChildNode,
    }),
    [],
  );

  const handleInit = useCallback((instance: ReactFlowInstance) => {
    setReactFlowInstance(instance);
  }, []);

  const fitView = useCallback(() => {
    setTimeout(() => {
      reactFlowInstance?.fitView({ padding: 0.22 });
    }, 100);
  }, [reactFlowInstance]);

  const handleExport = useCallback(() => {
    const exportedData = new LineageExporter(nodes, edges).exportLineage(
      highlightedNode?.id,
    );

    if (!exportedData) return;
    const url = window.URL.createObjectURL(new Blob([exportedData as string]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'lineage.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
  }, [nodes, edges, highlightedNode]);

  const parentNodesCount = useMemo(
    () => graphNodes.filter((n) => !n.parentNode).length,
    [graphNodes],
  );

  const handleNodeClick = useCallback(
    (e: any, node: ReactFlowNode<ReactFlowNodeData>) => {
      const isExpandButton = !!(
        e.target.closest && e.target.closest('[id*="expand-button"]')
      );

      const isNoneExpendableChildrenHeader = !!(
        e.target.closest && e.target.closest('[id="children-header-static"]')
      );

      const isSearchField = e.target.id.includes('search-field');

      if (isSearchField || isNoneExpendableChildrenHeader) {
        return;
      }

      // React Flow passes the node as rendered, which in pill form has a
      // shifted position that must not leak back into the graph state.
      const graphNode = graphNodes.find((n) => n.id === node.id) ?? node;

      if (isExpandButton) {
        onToggleExpandNode(graphNode);
        return;
      }

      onToggleHighlightNode(graphNode);
    },
    [graphNodes, onToggleExpandNode, onToggleHighlightNode],
  );

  const handleNodeKeyUp = useCallback(
    (e: any) => {
      const isSearchField = e.target.id.includes('search-field');
      if (isSearchField) {
        const eventData = JSON.parse(e.target.id);
        const { nodeId } = eventData;
        const searchVal = e.target.value;

        const node = graphNodes.find((n) => n.id === nodeId);
        if (node) {
          columnSearchByNodeId.current[nodeId] = searchVal;
          const filteredNodes = filterReactFlowChildNodes(
            node,
            searchVal,
            graphNodes,
          );

          setGraphNodes(filteredNodes);
        }
      }
    },
    [graphNodes, setGraphNodes, columnSearchByNodeId],
  );

  const shouldBlockGraphLoad = hitGraphLimits && !forceLoadLargeGraph;

  useEffect(() => {
    columnSearchByNodeId.current = {};
    setGraphNodes(nodes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes]);

  useEffect(() => {
    fitView();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentNodesCount]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => onToggleHighlightNode(undefined), []);

  useEffect(() => {
    if (dagDepthBoundary) {
      onDagDepthBoundaryChange?.(dagDepthBoundary);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dagDepthBoundary]);

  const serializedExceeded = JSON.stringify(exceededLimitParentAssets ?? []);
  useEffect(() => {
    setExceededColumnLimitAssets(
      new Set(JSON.parse(serializedExceeded) as string[]),
    );
  }, [serializedExceeded, setExceededColumnLimitAssets]);

  useEffect(() => {
    const nextNodeDegrees = getLineageNodeDegrees(edges);
    setNodeDegrees((prevNodeDegrees) =>
      areLineageNodeDegreesEqual(prevNodeDegrees, nextNodeDegrees)
        ? prevNodeDegrees
        : nextNodeDegrees,
    );
  }, [edges, setNodeDegrees]);

  useSyncCompactNodeInternals(graphNodes);

  const zoomTier = useStore(selectLineageZoomTier);
  const zoomTierGraph = useMemo(
    () =>
      getLineageZoomTierGraph(
        graphNodes,
        edges,
        isControlled ? 'card' : zoomTier,
      ),
    [graphNodes, edges, isControlled, zoomTier],
  );

  const handlePaneClick = useCallback(
    () => onToggleHighlightNode(undefined),
    [onToggleHighlightNode],
  );

  const isFiltered = assetIds.length > 0 || columnIds.length > 0;

  const isEmpty =
    isFiltered &&
    nodes.length === 0 &&
    !lineageDataLoading &&
    !!reactFlowInstance &&
    !shouldBlockGraphLoad;

  useViewLoadTracking({
    name: 'Lineage graph',
    loadDeps: isControlled
      ? [null]
      : [lineageDataLoading || !isFiltered ? null : graphNodes],
    contextId: isControlled
      ? `override-${JSON.stringify({ assetIds, columnIds, hopLimit })}`
      : JSON.stringify(urlFilters) +
        String(streamDirection) +
        String(hopLimit) +
        (selectedTreeNode?.id ?? ''),
  });

  return (
    <Wrapper $isCompactTier={!isControlled && zoomTier !== 'card'}>
      <ReactFlow
        onInit={handleInit}
        nodes={zoomTierGraph.nodes}
        edges={zoomTierGraph.edges}
        onNodesChange={onNodesChange}
        fitView
        minZoom={0}
        nodeTypes={nodeTypes}
        maxZoom={nodes.length > zoomLimitThreshold ? zoomLimit : 2}
        onNodeClick={handleNodeClick}
        onPaneClick={handlePaneClick}
        onlyRenderVisibleElements
        connectOnClick={false}
        nodesFocusable={false}
        edgesFocusable={false}
        onKeyUp={handleNodeKeyUp}
      >
        {isFiltered && !isControlled && (
          <>
            <LineageGraphControls
              enableExport={!!highlightedNode?.id}
              isZoomDisabled={shouldBlockGraphLoad && !lineageDataLoading}
              onExport={handleExport}
            />
            <Legend />
          </>
        )}

        {controlled?.showOverlayControls && (
          <>
            <Legend selectedLabel={controlled.legendSelectedLabel} isCompact />
            <HopsControlOverlay>
              <StreamSelector
                hopLimit={controlled.overrideHopLimit}
                dagDepthBoundary={controlled.overrideDagDepthBoundary}
                onHopLimitChange={(val) => {
                  if (val) {
                    controlled.onHopLimitChange(val);
                  }
                }}
              />
            </HopsControlOverlay>
          </>
        )}

        <Background
          variant={BackgroundVariant.Dots}
          gap={25}
          size={3}
          style={{ opacity: 0.2, zIndex: -1 }}
        />

        {isEmpty && (
          <CenteringWrapper>
            <Chip label="No nodes found" color="warning" />
          </CenteringWrapper>
        )}

        {shouldBlockGraphLoad && isFiltered && !lineageDataLoading && (
          <InteractiveCenteringWrapper>
            <SelectDatasetPage
              onContinue={() => setForceLoadLargeGraph(true)}
            />
          </InteractiveCenteringWrapper>
        )}

        {isFiltered && lineageDataLoading && (
          <CenteringWrapper>
            <CircularProgress sx={{ zIndex: 4 }} />
          </CenteringWrapper>
        )}
      </ReactFlow>
    </Wrapper>
  );
};

const LineageGraphRoot = (props: LineageGraphProps) => {
  const { reportData } = props;
  const isControlled = props.isControlled === true;
  const { selectedModelColumn } = useSelectedTreeItem();

  const columnFilterIds = useMemo(() => {
    if (isControlled && props.isControlled === true) {
      const ids = props.overrideColumnIds;
      return ids?.length ? new Set(ids) : undefined;
    }
    return selectedModelColumn ? new Set([selectedModelColumn]) : undefined;
  }, [isControlled, props, selectedModelColumn]);

  const overrideColorBy = props.isControlled
    ? props.overrideColorBy
    : undefined;

  return (
    <LineageGraphProvider
      reportData={reportData}
      isControlled={isControlled}
      columnFilterIds={columnFilterIds}
      overrideColorBy={overrideColorBy}
    >
      <ReactFlowProvider>
        <LineageGraphContent {...props} />
      </ReactFlowProvider>
      {!isControlled && <LineageInfoPanel />}
    </LineageGraphProvider>
  );
};

export default LineageGraphRoot;
